export type DrawTool = 'pen' | 'eraser';

export interface DrawPoint {
  x: number;
  y: number;
}

// Pontos normalizados (0..1) em relação ao tamanho do canvas no momento do traço:
// o desenho se adapta a redimensionamentos do painel (ex.: girar o tablet) sem sair da área.
export interface DrawingStroke {
  tool: DrawTool;
  size: number;
  points: DrawPoint[];
}

const DRAWING_STORAGE_PREFIX = 'simuladoNotesDrawing-';

export const getDrawingStorageKey = (lessonId: number | string) =>
  `${DRAWING_STORAGE_PREFIX}${lessonId}`;

// 4 casas decimais em coordenadas normalizadas dão precisão sub-pixel em telas de até ~4k px
// e reduzem o payload serializado à metade
export const roundCoord = (value: number) => Math.round(value * 1e4) / 1e4;

// Mesma política do texto: sessionStorage pode lançar (Safari em navegação privada, storage
// bloqueado por política); como o desenho é conveniência, degradamos para "só em memória".
export const safeGetDrawing = (lessonId: number | string): DrawingStroke[] => {
  try {
    const raw = sessionStorage.getItem(getDrawingStorageKey(lessonId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed?.strokes)) return [];
    // Descarta itens malformados: um traço sem points quebraria o redraw do canvas
    return parsed.strokes.filter((stroke: DrawingStroke) => Array.isArray(stroke?.points));
  } catch {
    return [];
  }
};

// Retorna se a gravação funcionou: cota cheia/storage indisponível é sinalizado ao aluno
// pelo painel em vez de falhar em silêncio
export const safeSetDrawing = (lessonId: number | string, strokes: DrawingStroke[]): boolean => {
  try {
    sessionStorage.setItem(
      getDrawingStorageKey(lessonId),
      JSON.stringify({ version: 1, strokes }),
    );
    return true;
  } catch {
    return false;
  }
};

// Remove o desenho do simulado ao finalizar a prova (não persiste após o encerramento)
export const clearSimuladoDrawing = (lessonId: number | string) => {
  try {
    sessionStorage.removeItem(getDrawingStorageKey(lessonId));
  } catch {
    // sem storage disponível: nada a limpar
  }
};

// Pointer Events + canvas 2d são os requisitos do modo desenho; sem eles o painel
// permanece só com o modo texto (dispositivos sem suporte seguem funcionando normalmente).
export const isDrawingSupported = () => {
  if (typeof window === 'undefined' || !window.PointerEvent) return false;
  try {
    return Boolean(document.createElement('canvas').getContext('2d'));
  } catch {
    return false;
  }
};
